import { useEffect } from 'react'

import { isJessicaSite } from 'common/constants/siteSelectorConstants'

import { useGetTeamMembersQuery } from 'features/TeamChat/api/teamChat.api'
import type { TeamMember } from 'features/TeamChat/api/teamChat.types'

export const MEMBERS_RETRY_MS = 60_000
const NOT_IN_TEAM = 403

export type TeamChatAccess = {
  enabled: boolean
  me?: TeamMember
  members: TeamMember[]
  doctorId?: number
}

type Args = { siteCode?: string; isLoggedIn: boolean }

// 403 means "no team" and stays off; any other failure is retried.
export const useTeamChatAccess = ({ siteCode, isLoggedIn }: Args): TeamChatAccess => {
  const siteAllowed = isLoggedIn && isJessicaSite(siteCode)
  const { data, error, refetch } = useGetTeamMembersQuery(undefined, { skip: !siteAllowed })
  const forbidden = (error as { status?: unknown } | undefined)?.status === NOT_IN_TEAM
  const retry = siteAllowed && Boolean(error) && !forbidden

  useEffect(() => {
    if (!retry) return
    const timer = setInterval(refetch, MEMBERS_RETRY_MS)
    return () => clearInterval(timer)
  }, [retry, refetch])

  // RTK keeps the last roster on a failed refetch, so a 403 must drop it explicitly.
  const allowed = siteAllowed && !forbidden
  const members = allowed ? data?.members ?? [] : []
  const me = members.find((m) => m.is_me)
  return {
    enabled: allowed && Boolean(me),
    me,
    members,
    doctorId: allowed ? data?.doctor_id : undefined,
  }
}
